import { LBtn, LModal, LTextareaField } from "@components";
import { faCheckCircle, faClipboard } from "@fortawesome/free-regular-svg-icons";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { AssessmentStatus } from "@store/generalTypes";
import { MouseEvent, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { translate, Translate } from "react-i18nify";
import { object, string } from "yup";
import { Env } from "../../../../../env";
import "./DownloadFollowupButtons.css";

const performanceGuideFileName = "LASSI-D_Reading the Performance Report.pdf";
const performanceGuideUrl = `${Env.REACT_APP_BLOB_URL}/documentations/${encodeURIComponent(performanceGuideFileName)}`;

interface MarkNote {
    note?: string;
}

export const DownloadFollowupButtons = ({ ...props }: DownloadFollowupButtonsProps): JSX.Element => {
    const validateMethods = useForm<MarkNote>({
        resolver: yupResolver(
            object({
                note: string().max(500).required(translate("admin.pleaseAddAnote"))
            })
        )
    });

    const [markForOpen, setMarkForOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [downloadingGuide, setDownloadingGuide] = useState(false);
    const [guideDownloadFailed, setGuideDownloadFailed] = useState(false);

    const downloadGuide = async (event: MouseEvent<HTMLAnchorElement>): Promise<void> => {
        event.preventDefault();
        if (downloadingGuide) return;
        setDownloadingGuide(true);
        setGuideDownloadFailed(false);
        try {
            const response = await fetch(performanceGuideUrl);
            if (!response.ok) throw new Error(`Guide download failed: ${response.status}`);
            const url = URL.createObjectURL(await response.blob());
            const link = document.createElement("a");
            link.href = url;
            link.download = performanceGuideFileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Allow the browser to start the download before releasing the object URL.
            window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
            setGuideDownloadFailed(true);
        } finally {
            setDownloadingGuide(false);
        }
    };

    const toggleModal = (): void => {
        validateMethods.resetField("note");
        setMarkForOpen(!markForOpen);
    };

    const handleOnConfirm = async (): Promise<void> => {
        setLoading(true);
        const valid = await validateMethods.trigger("note");

        if (!valid) {
            setLoading(false);
            return;
        }

        try {
            await props.handleChangeStatus(validateMethods.getValues("note")!);
            toggleModal();
        } catch (er) {
            console.error(er);
        } finally {
            setLoading(false);
        }
    };

    const isReviewNeeded = props.status?.toLowerCase() === AssessmentStatus.ReviewNeeded.replace(" ", "").toLowerCase();
    const isComplete =
        props.status?.toLowerCase() === AssessmentStatus.Complete.toLowerCase() ||
        props.status?.toLowerCase() === AssessmentStatus.ReviewComplete.replace(" ", "").toLowerCase();

    return (
        <>
            <a
                href={performanceGuideUrl}
                download={performanceGuideFileName}
                onClick={downloadGuide}
                aria-disabled={downloadingGuide}
                className={`button is-rounded is-outlined is-primary is-fullwidth mb-2 performance-guide-download${
                    downloadingGuide ? " is-loading" : ""
                }`}
            >
                <span className="is-size-6">
                    <Translate value="admin.downloadPerformanceGuide" />
                </span>
                <span className="icon">
                    <FontAwesomeIcon icon={faDownload} />
                </span>
            </a>
            {guideDownloadFailed && (
                <p role="alert" className="has-text-danger mb-2">
                    <Translate value="admin.guideDownloadFailed" />
                </p>
            )}
            <LBtn
                variation="outline"
                noSpacing
                fullWidth
                iconRight={faDownload}
                classes="mb-2 results-download"
                onClick={props.handlePrint}
            >
                <Translate value="admin.downloadPDF" />
            </LBtn>
            {isComplete && (
                <LBtn
                    variation="outline"
                    noSpacing
                    fullWidth
                    classes="assessment-review-action"
                    iconRight={faClipboard}
                    onClick={toggleModal}
                >
                    <Translate value="admin.markForReview" />
                </LBtn>
            )}

            {isReviewNeeded && (
                <LBtn
                    variation="outline"
                    noSpacing
                    fullWidth
                    classes="assessment-review-action"
                    iconRight={faCheckCircle}
                    onClick={toggleModal}
                >
                    <Translate value="admin.reviewCompleted" />
                </LBtn>
            )}

            {markForOpen && (
                <LModal
                    title={
                        <p className="has-text-centered has-text-weight-bold is-size-3">
                            <Translate value={isReviewNeeded ? "admin.reviewCompleted" : "admin.markForReview"} />
                        </p>
                    }
                    fullHeight={false}
                    width={"m"}
                    hasCancelConfirmButtons
                    confirmText={translate("done")}
                    onConfirm={handleOnConfirm}
                    onClose={toggleModal}
                    confirmClasses="is-danger"
                    confirmLoading={loading}
                    divider={false}
                >
                    <div className="is-flex is-flex-direction-column">
                        <>
                            {isComplete && (
                                <p className="has-text-centered mb-4">
                                    <Translate value="admin.reviewDetailTitle" />
                                </p>
                            )}
                            <FormProvider {...validateMethods}>
                                <LTextareaField
                                    label=""
                                    inputName="note"
                                    placeholder={
                                        isReviewNeeded
                                            ? translate("admin.reviewCompletePlaceholder")
                                            : translate("admin.reviewNotePlaceholder")
                                    }
                                    rows={10}
                                    maxLength={500}
                                    counter
                                    theme="dark"
                                    footer={translate("admin.invalidateReview3")}
                                />
                            </FormProvider>
                            {isReviewNeeded && (
                                <p className="has-text-centered mt-6 mb-2 has-text-weight-bold">
                                    <Translate value="admin.reviewCompleteDoneText" />
                                </p>
                            )}
                        </>
                    </div>
                </LModal>
            )}
        </>
    );
};

interface DownloadFollowupButtonsProps {
    handlePrint: () => void;
    status?: AssessmentStatus;
    handleChangeStatus: (note: string) => Promise<void>;
}
